import type { FC } from 'react';
import React from 'react';
import type { TooltipRenderProps } from 'react-joyride';
import ArrowBackIosRoundedIcon from '@mui/icons-material/ArrowBackIosRounded';
import ArrowForwardIosRoundedIcon from '@mui/icons-material/ArrowForwardIosRounded';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import {
  Box,
  Button,
  Divider,
  Grid,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import type { TFunction } from 'i18next';

import LanguageSwitcher from 'src/components/common/LanguageSwitcher';
import { FIButton } from 'src/components/followUpInspirations/components/FIButton';
import { FollowUpActionButton } from 'src/components/myFollowUp/FollowUpActionButton';

import { styles } from './guidedTourTooltip.styles';

const GuidedTourTooltip: FC<TooltipRenderProps> = ({
  continuous,
  index,
  isLastStep,
  size,
  step,
  backProps,
  closeProps,
  primaryProps,
}) => {
  const headline = step.data.headline || '';
  const image = step.data.image || '';
  const t = (step.data.t || ((s) => s)) as TFunction;
  const followUpLinks = !!step.data.followUpLinks;
  const { content } = step;

  return (
    <Box sx={styles.tooltip}>
      <Stack direction="row" sx={styles.headline}>
        <Typography variant="h5" sx={{ m: 'auto 0' }}>
          {headline}
        </Typography>
        <Stack direction="row">
          {index === 0 && (
            <Grid sx={styles.langSwitcher}>
              <LanguageSwitcher color="neutral" />
            </Grid>
          )}
          <IconButton {...closeProps} sx={styles.closeButton}>
            <CloseRoundedIcon />
          </IconButton>
        </Stack>
      </Stack>
      <Stack direction="column" spacing={2} sx={styles.body}>
        {!!image && (
          <Box sx={styles.image}>
            <img src={image} alt={t('guidedTour.imageAlt')} />
          </Box>
        )}
        <Box sx={styles.content}>{content}</Box>
        {followUpLinks && (
          <Stack direction="column" spacing={1}>
            <Box>
              <FollowUpActionButton color="secondary" />
            </Box>
            <Box>
              <FIButton hideIcon color="secondary" />
            </Box>
          </Stack>
        )}
        <Divider sx={styles.divider} />
        {continuous &&
          (index > 0 ? (
            <Stack direction="row" sx={styles.stepCounter}>
              <Button {...backProps} variant="squareCta" disabled={index <= 0}>
                <ArrowBackIosRoundedIcon />
              </Button>
              {Array(size - 1)
                .fill(0)
                .map((_, i) => (
                  <Stack
                    key={i}
                    direction="column"
                    sx={{
                      justifyContent: 'center',
                    }}
                  >
                    <Box sx={styles.stepDot(i === index - 1)} />
                  </Stack>
                ))}
              <Button {...primaryProps} variant="squareCta">
                {isLastStep ? (
                  <CheckCircleOutlineRoundedIcon />
                ) : (
                  <ArrowForwardIosRoundedIcon />
                )}
              </Button>
            </Stack>
          ) : (
            <Box>
              <Button {...primaryProps} variant="cta" fullWidth>
                {t('guidedTour.start')}
              </Button>
            </Box>
          ))}
      </Stack>
    </Box>
  );
};

export default GuidedTourTooltip;
